import React, { useState, useEffect } from "react";
import { Form, Button, Row, Col } from "react-bootstrap";
import { AgGridReact } from "ag-grid-react";
import { CiEdit } from "react-icons/ci";
import { MdOutlineDeleteOutline } from "react-icons/md";
import axiosOrganizationInstance from "./AxiosOrganizationInstance";
import { confirmAction } from "../restaurant/ui/feedback";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";

function OrganizationFeatures({ organizationId, status, onFeaturesUpdate }) {
    const [featuresList, setFeaturesList] = useState([]);
    const [selectedFeature, setSelectedFeature] = useState(null);
    const [isFeatureEditing, setIsFeatureEditing] = useState(false);
    const [isFeatureFormVisible, setIsFeatureFormVisible] = useState(false);
    const [featureForm, setFeatureForm] = useState({
        category: "",
        featureName: ""
    });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (organizationId) {
            fetchFeatures();
        }
    }, [organizationId]);

    const fetchFeatures = async () => {
        if (!organizationId || organizationId === "null") {
            setLoading(false);
            return;
        }

        try {
            setLoading(true);
            const token = localStorage.getItem("Token");
            const response = await axiosOrganizationInstance.get(
                `/organization/${organizationId}/features`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            const featuresData = response.data || [];
            // Convert grouped format to flat list for table
            const flattenedFeatures = featuresData.flatMap(categoryGroup => 
                categoryGroup.features.map(feature => ({
                    featureId: feature.featureId,
                    category: categoryGroup.category,
                    featureName: feature.featureName
                }))
            );
            setFeaturesList(flattenedFeatures);

            // Notify parent component if callback provided
            if (onFeaturesUpdate) {
                // Convert to HTML format for parent
                if (featuresData.length > 0) {
                    const htmlContent = featuresData.map(categoryGroup => {
                        const categoryName = categoryGroup.category;
                        const featuresList = categoryGroup.features.map(feature => 
                            `<p data-feature-id="${feature.featureId || ''}">${feature.featureName}</p>`
                        ).join('');
                        return `<p><strong>${categoryName}:</strong></p>${featuresList}`;
                    }).join('');
                    onFeaturesUpdate(htmlContent);
                } else {
                    onFeaturesUpdate("");
                }
            }
        } catch (error) {
            console.error("Error fetching features:", error);
            setFeaturesList([]);
        } finally {
            setLoading(false);
        }
    };

    const handleFeatureSubmit = async (e) => {
        e.preventDefault();
        try {
            const token = localStorage.getItem("Token");
            if (isFeatureEditing && selectedFeature) {
                // Update existing feature using PUT
                await axiosOrganizationInstance.put(
                    `/organization/features/${selectedFeature.featureId}`,
                    {
                        category: featureForm.category,
                        featureName: featureForm.featureName
                    },
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                            'Content-Type': 'application/json',
                        },
                    }
                );
                alert('Feature updated successfully');
            } else {
                // Create new feature using POST
                await axiosOrganizationInstance.post(
                    `/organization/${organizationId}/features`,
                    [{
                        category: featureForm.category,
                        featureName: featureForm.featureName
                    }],
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                            'Content-Type': 'application/json',
                        },
                    }
                );
                alert('Feature added successfully');
            }
            
            // Refresh features
            fetchFeatures();
            
            // Reset form
            setIsFeatureFormVisible(false);
            setIsFeatureEditing(false);
            setFeatureForm({ category: "", featureName: "" });
            setSelectedFeature(null);
        } catch (error) {
            console.error("Error saving feature:", error);
            alert("Failed to save feature. Please try again.");
        }
    };

    const handleDeleteFeature = async (featureId) => {
        const ok = await confirmAction({
            title: "Delete feature?",
            message: "Are you sure you want to delete this feature?",
            confirmLabel: "Delete",
            danger: true,
        });
        if (!ok) return;
        
        try {
            const token = localStorage.getItem("Token");
            await axiosOrganizationInstance.delete(
                `/organization/features/${featureId}`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );
            alert('Feature deleted successfully');
            
            // Refresh features
            fetchFeatures();
        } catch (error) {
            console.error("Error deleting feature:", error);
            alert("Failed to delete feature. Please try again.");
        }
    };

    if (loading) {
        return <div>Loading features...</div>;
    }

    return (
        <div style={{ marginTop: "30px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "15px" }}>
                <h5 style={{ margin: 0 }}>Features</h5>
                {!isFeatureFormVisible && (
                    <Button
                        variant="outline-dark"
                        onClick={() => {
                            setIsFeatureFormVisible(true);
                            setIsFeatureEditing(false);
                            setFeatureForm({ category: "", featureName: "" });
                        }}
                        className="me-2"
                        disabled={status === "INACTIVE"}
                        style={{
                            backgroundColor: status === "INACTIVE" ? "#e9ecef" : "#FFFEF0",
                            border: "1px solid #0976A1",
                            color: status === "INACTIVE" ? "#6c757d" : "#017EF3",
                            cursor: status === "INACTIVE" ? "not-allowed" : "pointer",
                        }}
                    >
                        Add Feature
                    </Button>
                )}
            </div>

            {featuresList.length > 0 ? (
                <div
                    className="ag-theme-quartz"
                    style={{ 
                        width: "100%", 
                        marginBottom: "20px"
                    }}
                >
                    <AgGridReact
                        columnDefs={[
                            {
                                headerName: "Category",
                                field: "category",
                                suppressMovable: true,
                                width: 200,
                            },
                            {
                                headerName: "Feature Name",
                                field: "featureName",
                                suppressMovable: true,
                                flex: 1,
                            },
                            {
                                headerName: "Actions",
                                width: 150,
                                cellRenderer: (params) => {
                                    return (
                                        <div
                                            className="d-flex justify-content-center align-items-center"
                                            style={{ marginTop: "12px", gap: "15px" }}
                                        >
                                            <CiEdit
                                                onClick={() => {
                                                    if (status !== "INACTIVE") {
                                                        setSelectedFeature(params.data);
                                                        setFeatureForm({
                                                            category: params.data.category,
                                                            featureName: params.data.featureName
                                                        });
                                                        setIsFeatureEditing(true);
                                                        setIsFeatureFormVisible(true);
                                                    }
                                                }}
                                                style={{
                                                    fontSize: "24px",
                                                    color: status === "INACTIVE" ? "#6c757d" : "#017EF3",
                                                    cursor: status === "INACTIVE" ? "not-allowed" : "pointer",
                                                    opacity: status === "INACTIVE" ? 0.5 : 1,
                                                }}
                                            />
                                            <MdOutlineDeleteOutline
                                                onClick={() => {
                                                    if (status !== "INACTIVE") {
                                                        handleDeleteFeature(params.data.featureId);
                                                    }
                                                }}
                                                style={{
                                                    fontSize: "24px",
                                                    color: status === "INACTIVE" ? "#6c757d" : "#FF3B30",
                                                    cursor: status === "INACTIVE" ? "not-allowed" : "pointer",
                                                    opacity: status === "INACTIVE" ? 0.5 : 1,
                                                }}
                                            />
                                        </div>
                                    );
                                },
                                suppressMovable: true,
                            },
                        ]}
                        rowData={featuresList}
                        pagination={false}
                        paginationPageSize={10}
                        domLayout="autoHeight"
                    />
                </div>
            ) : (
                <div style={{ 
                    padding: "20px", 
                    textAlign: "center", 
                    color: "#6c757d",
                    border: "1px dashed #ddd",
                    borderRadius: "4px"
                }}>
                    No features added yet. Click "Add" to add.
                </div>
            )}

            {isFeatureFormVisible && (
                <div style={{ 
                    border: "1px solid #ddd", 
                    borderRadius: "4px", 
                    padding: "20px", 
                    marginBottom: "20px",
                    backgroundColor: "#f9f9f9"
                }}>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h5 style={{ margin: 0 }}>{isFeatureEditing ? "Edit Feature" : "Add New Feature"}</h5>
                    </div>
                    <Form onSubmit={handleFeatureSubmit}>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Category</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={featureForm.category}
                                        onChange={(e) => setFeatureForm({ ...featureForm, category: e.target.value })}
                                        placeholder="Enter category"
                                        disabled={status === "INACTIVE"}
                                        required
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Feature Name</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={featureForm.featureName}
                                        onChange={(e) => setFeatureForm({ ...featureForm, featureName: e.target.value })}
                                        placeholder="Enter feature name"
                                        disabled={status === "INACTIVE"}
                                        required
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                        <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", marginTop: "10px" }}>
                            <Button
                                variant="outline-dark"
                                onClick={() => {
                                    setIsFeatureFormVisible(false);
                                    setIsFeatureEditing(false);
                                    setFeatureForm({ category: "", featureName: "" });
                                    setSelectedFeature(null);
                                }}
                                disabled={status === "INACTIVE"}
                                style={{
                                    width: "90px",
                                    backgroundColor: status === "INACTIVE" ? "#e9ecef" : "#FFFEF0",
                                    border: "1px solid #0976A1",
                                    color: status === "INACTIVE" ? "#6c757d" : "#017EF3",
                                    cursor: status === "INACTIVE" ? "not-allowed" : "pointer",
                                }}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="outline-dark"
                                type="submit"
                                disabled={status === "INACTIVE"}
                                style={{
                                    width: "90px",
                                    backgroundColor: status === "INACTIVE" ? "#e9ecef" : "#FFFEF0",
                                    border: "1px solid #0976A1",
                                    color: status === "INACTIVE" ? "#6c757d" : "#017EF3",
                                    cursor: status === "INACTIVE" ? "not-allowed" : "pointer",
                                }}
                            >
                                {isFeatureEditing ? "Update" : "Add"}
                            </Button>
                        </div>
                    </Form>
                </div>
            )}
        </div>
    );
}

export default OrganizationFeatures;

